import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

export type SalaryDisplayMode = 'converted' | 'original';

const STORAGE_KEY = 'salaryDisplayMode';

interface SalaryDisplayPreferenceContextValue {
  mode: SalaryDisplayMode;
  setMode: (mode: SalaryDisplayMode) => void;
  toggle: () => void;
}

/**
 * Whether SRP job cards show salary converted to monthly EUR (default, matches the search
 * index's sortable/filterable salaryRangeMin/Max) or in the original as-advertised currency and
 * pay period. Client-only (localStorage), not read during SSR — the static/first-hydrated paint
 * always shows converted, same as every visitor gets today; a returning visitor's "original"
 * choice applies after mount. Cards without original-currency data (see
 * useMarketHeaderData-adjacent HitCompactViewProps.originalSalaryRangeMin) fall back to
 * converted regardless of this preference.
 */
const SalaryDisplayPreferenceContext = createContext<SalaryDisplayPreferenceContextValue>({
  mode: 'converted',
  setMode: () => {},
  toggle: () => {},
});

export function SalaryDisplayPreferenceProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [mode, setModeState] = useState<SalaryDisplayMode>('converted');

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === 'original' || stored === 'converted') {
        setModeState(stored);
      }
    } catch {
      // localStorage unavailable (private browsing, etc.) — stay on the default.
    }
  }, []);

  const setMode = useCallback((next: SalaryDisplayMode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Best-effort persistence only.
    }
  }, []);

  const toggle = useCallback(() => {
    setMode(mode === 'original' ? 'converted' : 'original');
  }, [mode, setMode]);

  return (
    <SalaryDisplayPreferenceContext.Provider value={{ mode, setMode, toggle }}>
      {children}
    </SalaryDisplayPreferenceContext.Provider>
  );
}

export function useSalaryDisplayPreference(): SalaryDisplayPreferenceContextValue {
  return useContext(SalaryDisplayPreferenceContext);
}
